import React, { FC, useCallback, useState } from 'react'
import { NumberFormatValues, PatternFormat } from 'react-number-format'
import { CountryCode } from 'libphonenumber-js'
import { BtInputBase, BtInputBaseProps } from './BtInputBase'
import ErrorTextComponent from './ErrorTextComponent'

export interface PhoneInputProps
  extends Omit<BtInputBaseProps, 'onChangeText'> {
  countryCode?: CountryCode
  onChangeText: (value: PhoneInputValue) => void
  onBlur?: () => void
}

export interface PhoneInputValue {
  raw: string
  formatted: string
  parsed: string
}

const removeInternationalPrefix = (phoneNumber: string) => {
  if (phoneNumber.startsWith('+1')) {
    return phoneNumber.slice(2)
  }
  return phoneNumber
}

export const PhoneInputWithParentheses: FC<PhoneInputProps> = ({
  size = 'large',
  value,
  label,
  labelStyle,
  textStyle,
  required,
  disabled,
  placeholder,
  validate,
  onChangeText,
  onBlur,
  testID,
  style,
}) => {
  const [phone, setPhone] = useState<string>(
    removeInternationalPrefix(value ?? ''),
  )

  const handleValidate: (text: string) => string | undefined = useCallback(
    (text: string) => {
      if (validate) {
        return validate(text)
      }
      return undefined
    },
    [validate],
  )

  const handleValueChange = (formatValues: NumberFormatValues) => {
    const cleanValue = formatValues.value.replace(/[^\d]/g, '')
    const formattedValue = formatPhoneNumber(cleanValue)

    setPhone(formattedValue)

    const result = {
      raw: cleanValue,
      formatted: formattedValue,
      parsed: cleanValue,
    }

    if (onChangeText) {
      onChangeText(result)
    }
  }

  const formatPhoneNumber = (val: string): string => {
    if (!val.length) {
      return ''
    } else if (val.length <= 3) {
      return `(${val}`
    } else if (val.length <= 6) {
      return `(${val.slice(0, 3)}) ${val.slice(3)}`
    } else if (val.length <= 10) {
      return `(${val.slice(0, 3)}) ${val.slice(3, 6)}-${val.slice(6)}`
    }
    return val.slice(0, 10)
  }

  const captionComponent = <ErrorTextComponent text={''} />

  return (
    <PatternFormat
      format={'(###) ###-####'}
      placeholder={placeholder}
      size={size}
      label={label}
      labelStyle={labelStyle}
      textStyle={textStyle}
      value={phone}
      required={required}
      disabled={disabled}
      keyboardType="number-pad"
      customInput={BtInputBase}
      validate={handleValidate}
      allowEmptyFormatting={false}
      onValueChange={handleValueChange}
      onChangeText={handleValidate}
      testID={testID}
      onBlur={onBlur}
      validateEmptyStateOnBlur={true}
      keepValidationStateOnFocus={true}
      captionComponent={captionComponent}
      style={style}
    />
  )
}
